import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DndContext, pointerWithin, useDraggable, useDroppable } from '@dnd-kit/core';
import {
  Box, Button, Checkbox, Chip, CircularProgress, InputAdornment, Stack, TextField, Typography
} from '@mui/material';
import {
  DragIndicator as DragIcon, Search as SearchIcon, Business as BusinessIcon,
  DeleteSweep as ClearIcon
} from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import {
  ASSIGN_USERS_TO_ORGANIZATION, ORGANIZATION_MEMBERS, REMOVE_USERS_FROM_ORGANIZATION
} from 'api/graphql/organization';

// Traemos todos los clientes con su estado y plan; el filtrado (activos/mora/nuevos, ocultando
// abandonadores) se hace en el cliente para poder distinguir "nuevo sin plan" de "inactivo".
const ASSIGN_CLIENTS = gql`
  query AssignClients {
    users(role: CLIENT, first: 300, page: 1, orderBy: { column: FIRST_NAME, order: ASC }) {
      data {
        id
        full_name
        email
        membership_status
        plan_id { id }
        organization { id name }
      }
    }
  }
`;

const STATUS_META = {
  ACTIVE: { label: 'Activo', bg: 'rgba(27,147,75,0.16)', color: '#5FE0A0' },
  IN_ARREARS: { label: 'En mora', bg: 'rgba(249,189,78,0.16)', color: 'var(--otx-gold-text)' },
  NEW: { label: 'Nuevo', bg: 'rgba(122,174,255,0.16)', color: '#7AAEFF' }
};

// "Nuevo" = sin plan asignado aún (INACTIVE pero nunca pagó). Se puede asignar a una organización.
const isNewNoPlan = (c) => c.membership_status === 'INACTIVE' && !c.plan_id?.id;

function DraggableClient({ client, checked, onToggle }) {
  const meta = isNewNoPlan(client) ? STATUS_META.NEW : STATUS_META[client.membership_status];
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `user-${client.id}`, data: { userId: client.id }
  });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 1200 } : undefined;
  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1, py: 0.75, borderRadius: 1.5,
        border: '1px solid', borderColor: isDragging ? 'gold.main' : 'hairline.main',
        backgroundColor: 'surface.content'
      }}
    >
      <Checkbox size="small" checked={checked} onChange={() => onToggle(client.id)} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{client.full_name}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {client.email}{client.organization?.name ? ` · ${client.organization.name}` : ''}
        </Typography>
      </Box>
      {meta && <Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700 }} />}
      <Box {...listeners} {...attributes} sx={{ cursor: 'grab', color: 'text.secondary', display: 'flex' }}>
        <DragIcon fontSize="small" />
      </Box>
    </Stack>
  );
}

DraggableClient.propTypes = {
  client: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};

function DropZone({ members, onRemove, onRemoveAll, loading }) {
  const { t } = useTranslation(['organizations']);
  const { setNodeRef, isOver } = useDroppable({ id: 'org-dropzone' });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 2, minHeight: 120, borderRadius: 2,
        border: '2px dashed', borderColor: isOver ? 'gold.main' : 'hairline.strong',
        backgroundColor: isOver ? 'gold.soft' : 'surface.overlay',
        transition: 'all 150ms ease'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: members.length ? 1.5 : 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', minWidth: 0 }}>
          <BusinessIcon sx={{ fontSize: 18, color: isOver ? 'var(--otx-gold-text)' : 'text.secondary' }} />
          <Typography variant="body2" noWrap>
            {members.length
              ? t('membersInOrg', { defaultValue: 'Miembros de la organización ({{n}})', n: members.length })
              : t('dropHere', { defaultValue: 'Arrastra clientes aquí para asignarlos a esta organización' })}
          </Typography>
          {loading && <CircularProgress size={14} sx={{ color: 'gold.main' }} />}
        </Stack>
        {members.length > 0 && (
          <Button size="small" variant="text" startIcon={<ClearIcon />} onClick={onRemoveAll} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', color: 'gold.main' }}>
            {t('removeAll', { defaultValue: 'Quitar todos' })}
          </Button>
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {members.map((m) => (
          <Chip
            key={m.id}
            label={m.full_name}
            onDelete={() => onRemove(m.id)}
            sx={{ backgroundColor: 'surface.raised', color: 'text.primary', fontWeight: 600, '& .MuiChip-deleteIcon': { color: (th) => th.palette.deleteAction.fg } }}
          />
        ))}
      </Stack>
    </Box>
  );
}

DropZone.propTypes = {
  members: PropTypes.array.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRemoveAll: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

function OrganizationAssignBoard({ organizationId, onAssigned = null }) {
  const { t } = useTranslation(['organizations']);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  // Confirmación de quitar (chip individual o "Quitar todos"): guarda los ids a retirar.
  const [confirmRemove, setConfirmRemove] = useState(null);

  const { data, loading, refetch } = useQuery(ASSIGN_CLIENTS, { fetchPolicy: 'cache-and-network' });
  const { data: membersData, loading: loadingMembers, refetch: refetchMembers } = useQuery(ORGANIZATION_MEMBERS, {
    variables: { organization_id: [Number(organizationId)], first: 300, page: 1 },
    fetchPolicy: 'cache-and-network'
  });
  const [assignUsers, { loading: assigning }] = useMutation(ASSIGN_USERS_TO_ORGANIZATION);
  const [removeUsers, { loading: removing }] = useMutation(REMOVE_USERS_FROM_ORGANIZATION);

  const clients = data?.users?.data ?? [];
  const members = membersData?.users?.data ?? [];
  const orgIdStr = String(organizationId);

  // El listado inferior excluye a quienes ya están en esta org (se ven arriba) y a los INACTIVOS
  // que YA tuvieron plan (abandonaron). Los nuevos SIN plan sí aparecen (para poder habilitarlos).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (String(c.organization?.id) === orgIdStr) return false;
      if (c.membership_status === 'INACTIVE' && c.plan_id?.id) return false;
      if (!term) return true;
      return c.full_name?.toLowerCase().includes(term) || c.email?.toLowerCase().includes(term);
    });
  }, [clients, search, orgIdStr]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const afterChange = async () => {
    setSelected({});
    await Promise.all([refetch(), refetchMembers()]);
    if (onAssigned) onAssigned();
  };

  const assign = async (ids) => {
    if (!ids.length) return;
    await assignUsers({ variables: { organization_id: organizationId, user_ids: ids } });
    await afterChange();
  };

  const doRemove = async () => {
    const ids = confirmRemove || [];
    setConfirmRemove(null);
    if (!ids.length) return;
    await removeUsers({ variables: { user_ids: ids } });
    await afterChange();
  };

  const onDragEnd = ({ active, over }) => {
    if (over?.id !== 'org-dropzone') return;
    const draggedId = active?.data?.current?.userId;
    if (!draggedId) return;
    // Si el arrastrado está dentro de la selección múltiple, se asignan todos los seleccionados.
    const ids = selectedIds.includes(String(draggedId)) ? selectedIds : [draggedId];
    assign(ids);
  };

  return (
    <DndContext collisionDetection={pointerWithin} onDragEnd={onDragEnd}>
      <Stack spacing={2}>
        <DropZone
          members={members}
          onRemove={(id) => setConfirmRemove([id])}
          onRemoveAll={() => setConfirmRemove(members.map((m) => m.id))}
          loading={loadingMembers || removing}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder={t('searchClient', { defaultValue: 'Buscar cliente' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => assign(selectedIds)}
            disabled={assigning || selectedIds.length === 0}
            sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {t('assignSelected', { defaultValue: 'Asignar seleccionados' })} {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {loading && clients.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>
          )}
          <Stack spacing={0.75}>
            {filtered.map((c) => (
              <DraggableClient key={c.id} client={c} checked={Boolean(selected[c.id])} onToggle={toggle} />
            ))}
          </Stack>
          {!loading && filtered.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Chip icon={<SearchIcon />} label={t('noClients', { defaultValue: 'Sin resultados' })} variant="outlined" />
            </Box>
          )}
        </Box>
      </Stack>

      <ConfirmDeleteModal
        open={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        onConfirm={doRemove}
        title={t('removeMemberTitle', { defaultValue: 'Quitar de la organización' })}
        message={t('removeMemberMessage', { defaultValue: '¿Seguro que quieres quitar a {{count}} miembro(s)? Volverán a OTRIX App.', count: confirmRemove?.length || 0 })}
        confirmLabel={t('remove', { defaultValue: 'Quitar' })}
        note=""
      />
    </DndContext>
  );
}

OrganizationAssignBoard.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAssigned: PropTypes.func
};

export default OrganizationAssignBoard;
